import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { cloneDeep } from 'lodash'
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/outline'

import { findOrCreateBillingService, getCurrency, adaptChannelPricesToModality } from '../utils'
import { SERVICE_TYPES, TECH_FEE_SERVICE_TYPES } from '../constants'
import { fetchAvailableChannels } from '../../../services'
import RangeBillingPriceConfigTable from '../RangeBillingPriceConfigTable'
import { UNITS_IDS } from '../RangeBillingPriceConfigTable/constants'
import ChannelPricesTable from './ChannelPricesTable'

const TechFeeServices = ({ storeBillingServices, onUpdateStoreBillingServices, onIsValid, storeId, store, warehouse }) => {
  const { i18n } = useTranslation()

  // Only a store selling both ways prices each channel per business type.
  const requiresBusinessType = Boolean(store?.d2c) && Boolean(store?.b2b)

  const tableHeaders = [
    { label: i18n.t('billing.table_headers.from_piece'), id: 'from' },
    { label: i18n.t('billing.table_headers.to_piece'), id: 'to' },
    { label: i18n.t('billing.table_headers.price'), id: 'price' },
    { label: i18n.t('billing.table_headers.unit'), id: 'unit' },
  ]

  const [isOrderTechFeeValid, setIsOrderTechFeeValid] = useState(false)
  const [isReturnTechFeeValid, setIsReturnTechFeeValid] = useState(false)
  const [areOrderChannelPricesValid, setAreOrderChannelPricesValid] = useState(true)
  const [areReturnChannelPricesValid, setAreReturnChannelPricesValid] = useState(true)
  const hasAdaptedChannelPricesRef = useRef(false)

  const orderTechFeeService = findOrCreateBillingService({
    serviceType: SERVICE_TYPES.SERVICE_ORDER_TECH_FEE,
    billingServices: storeBillingServices,
    currency: getCurrency(warehouse?.country),
    storeId: storeId,
    warehouseId: warehouse?.id,
  })
  const returnTechFeeService = findOrCreateBillingService({
    serviceType: SERVICE_TYPES.SERVICE_RETURN_TECH_FEE,
    billingServices: storeBillingServices,
    currency: getCurrency(warehouse?.country),
    storeId: storeId,
    warehouseId: warehouse?.id,
  })

  const {
    data: availableChannelsData,
    isLoading: isLoadingChannels,
    isError: isChannelsError,
  } = useQuery(['store_billing_available_channels', storeId, warehouse?.id], () => fetchAvailableChannels({ store_id: storeId, warehouse_id: warehouse?.id }), {
    enabled: Boolean(storeId),
    refetchOnWindowFocus: false,
    retry: false,
  })

  const availableChannels = availableChannelsData?.channels || []

  const orderChannels = useMemo(
    () => availableChannels.filter(({ applies_to }) => !applies_to || applies_to.includes(SERVICE_TYPES.SERVICE_ORDER_TECH_FEE)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- availableChannels is derived from the query data
    [availableChannelsData],
  )

  const returnChannels = useMemo(
    () => availableChannels.filter(({ applies_to }) => !applies_to || applies_to.includes(SERVICE_TYPES.SERVICE_RETURN_TECH_FEE)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- availableChannels is derived from the query data
    [availableChannelsData],
  )

  const priceModesTechFee = [UNITS_IDS.TOTAL]

  const orderTechFeeTableData = orderTechFeeService?.price_configuration?.map(priceRange => {
    return {
      from: { value: priceRange.from, isDisabled: true },
      to: { value: priceRange.to, isDisabled: true },
      price: { value: priceRange.price },
      price_mode: { value: priceRange.price_mode || UNITS_IDS.TOTAL, isDisabled: true },
      excess: { value: priceRange.excess },
      range_exclusion: { value: priceRange.range_exclusion },
    }
  })

  const returnTechFeeTableData = returnTechFeeService?.price_configuration?.map(priceRange => {
    return {
      from: { value: priceRange.from, isDisabled: true },
      to: { value: priceRange.to, isDisabled: true },
      price: { value: priceRange.price },
      price_mode: { value: priceRange.price_mode || UNITS_IDS.TOTAL, isDisabled: true },
      excess: { value: priceRange.excess },
      range_exclusion: { value: priceRange.range_exclusion },
    }
  })

  const orderUnitOptionsLabels = {
    TOTAL: i18n.t('billing.tech_fee_services.order_unit_options.TOTAL'),
  }

  const returnUnitOptionsLabels = {
    TOTAL: i18n.t('billing.tech_fee_services.return_unit_options.TOTAL'),
  }

  const handleOnUpdateData = (serviceType, newTableData, service) => {
    let newStoreBillingServices = cloneDeep(storeBillingServices)

    const newPriceConfiguration = newTableData.map(priceRangeData => {
      return {
        from: priceRangeData.from.value,
        to: priceRangeData.to.value,
        price: priceRangeData.price.value,
        price_mode: priceRangeData.price_mode.value,
        excess: priceRangeData.excess.value,
        range_exclusion: priceRangeData.range_exclusion.value,
      }
    })

    const serviceExists = newStoreBillingServices.some(({ service_type }) => service_type === serviceType)

    if (serviceExists) {
      newStoreBillingServices = newStoreBillingServices.map(billingService => {
        if (billingService.service_type === serviceType) {
          billingService.price_configuration = newPriceConfiguration
        }
        return billingService
      })
    } else {
      const newService = cloneDeep(service)
      newService.price_configuration = newPriceConfiguration
      newStoreBillingServices.push(newService)
    }

    onUpdateStoreBillingServices(newStoreBillingServices)
  }

  const handleOnUpdateChannelPrices = (serviceType, newChannelPrices, service) => {
    let newStoreBillingServices = cloneDeep(storeBillingServices)

    const serviceExists = newStoreBillingServices.some(({ service_type }) => service_type === serviceType)

    if (serviceExists) {
      newStoreBillingServices = newStoreBillingServices.map(billingService => {
        if (billingService.service_type === serviceType) {
          billingService.channel_prices = newChannelPrices
        }
        return billingService
      })
    } else {
      const newService = cloneDeep(service)
      newService.channel_prices = newChannelPrices
      newStoreBillingServices.push(newService)
    }

    onUpdateStoreBillingServices(newStoreBillingServices)
  }

  // A duplicated configuration may come from a store with a different business model. This runs
  // once per mount: reshaping on every edit would fight the operator, re-seeding the pair of a
  // channel whose row was just deleted.
  useEffect(() => {
    if (!store || !storeBillingServices || hasAdaptedChannelPricesRef.current) {
      return
    }

    hasAdaptedChannelPricesRef.current = true

    let hasChanges = false

    const adaptedStoreBillingServices = storeBillingServices.map(billingService => {
      if (!TECH_FEE_SERVICE_TYPES.includes(billingService.service_type) || !billingService.channel_prices?.length) {
        return billingService
      }

      const adaptedChannelPrices = adaptChannelPricesToModality(billingService.channel_prices, requiresBusinessType)

      if (JSON.stringify(adaptedChannelPrices) === JSON.stringify(billingService.channel_prices)) {
        return billingService
      }

      hasChanges = true
      return { ...billingService, channel_prices: adaptedChannelPrices }
    })

    if (hasChanges) {
      onUpdateStoreBillingServices(adaptedStoreBillingServices)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs on the loaded template, not on every callback identity
  }, [storeBillingServices, requiresBusinessType, store])

  useEffect(() => {
    onIsValid(isOrderTechFeeValid && isReturnTechFeeValid && areOrderChannelPricesValid && areReturnChannelPricesValid)
  }, [isOrderTechFeeValid, isReturnTechFeeValid, areOrderChannelPricesValid, areReturnChannelPricesValid])

  return (
    <div className="flex flex-col gap-5">
      <div>
        <div className="flex gap-1">
          <h2 className="text-xl text-gray-500">
            <span className="text-red-500">*</span>
            {i18n.t('billing.tech_fee_services.order_tech_fee_title')}
          </h2>

          {isOrderTechFeeValid ? <CheckCircleIcon className="h-5 w-5 text-green-500" /> : <ExclamationCircleIcon className="h-5 w-5 text-yellow-500" />}
        </div>

        <RangeBillingPriceConfigTable
          headers={tableHeaders}
          priceConfiguration={orderTechFeeTableData}
          currency={getCurrency(warehouse?.country)}
          onUpdatePriceConfiguration={newTableData => handleOnUpdateData(SERVICE_TYPES.SERVICE_ORDER_TECH_FEE, newTableData, orderTechFeeService)}
          onIsValid={setIsOrderTechFeeValid}
          availableUnitOptions={priceModesTechFee}
          unitOptionsLabels={orderUnitOptionsLabels}
        />

        <ChannelPricesTable
          channelPrices={orderTechFeeService?.channel_prices || []}
          availableChannels={orderChannels}
          currency={getCurrency(warehouse?.country)}
          onUpdateChannelPrices={newChannelPrices => handleOnUpdateChannelPrices(SERVICE_TYPES.SERVICE_ORDER_TECH_FEE, newChannelPrices, orderTechFeeService)}
          onIsValid={setAreOrderChannelPricesValid}
          isLoadingChannels={isLoadingChannels}
          hasChannelsError={isChannelsError}
          requiresBusinessType={requiresBusinessType}
        />
      </div>

      <div>
        <div className="flex gap-1">
          <h2 className="text-xl text-gray-500">
            <span className="text-red-500">*</span>
            {i18n.t('billing.tech_fee_services.return_tech_fee_title')}
          </h2>

          {isReturnTechFeeValid ? <CheckCircleIcon className="h-5 w-5 text-green-500" /> : <ExclamationCircleIcon className="h-5 w-5 text-yellow-500" />}
        </div>

        <RangeBillingPriceConfigTable
          headers={tableHeaders}
          priceConfiguration={returnTechFeeTableData}
          currency={getCurrency(warehouse?.country)}
          onUpdatePriceConfiguration={newTableData => handleOnUpdateData(SERVICE_TYPES.SERVICE_RETURN_TECH_FEE, newTableData, returnTechFeeService)}
          onIsValid={setIsReturnTechFeeValid}
          availableUnitOptions={priceModesTechFee}
          unitOptionsLabels={returnUnitOptionsLabels}
        />

        <ChannelPricesTable
          channelPrices={returnTechFeeService?.channel_prices || []}
          availableChannels={returnChannels}
          currency={getCurrency(warehouse?.country)}
          onUpdateChannelPrices={newChannelPrices => handleOnUpdateChannelPrices(SERVICE_TYPES.SERVICE_RETURN_TECH_FEE, newChannelPrices, returnTechFeeService)}
          onIsValid={setAreReturnChannelPricesValid}
          isLoadingChannels={isLoadingChannels}
          hasChannelsError={isChannelsError}
          requiresBusinessType={requiresBusinessType}
        />
      </div>
    </div>
  )
}

export default TechFeeServices
